import {
  GatewayAssujetti,
  GatewayContact,
  GatewayEntreprise,
} from "@conformite/gateway";
import {
  Link,
  SkeletonTable,
  TagConformite,
  Button,
} from "@endya-conformite/ui-kit";
import ChevronLeft from "@endya-conformite/ui-kit/src/assets/icons/chevron-left.svg?react";
import { useAssujettiFilter } from "@src/assujettis/AssujettiMainList/useAssujettiFilter";
import { AssujettiList } from "@src/components/Assujetti/AssujettiList";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import {
  useHasForbiddenTypeAdhesion,
  useIsAllowed,
} from "@src/session/useIsAllowed";
import { StoreEntryFetchStatus } from "@src/store/store";
import { useAssujetti } from "@src/store/store.assujetti";
import { Assujetti } from "@src/store/store.definition";
import { useEntreprises } from "@src/store/store.entreprise";
import { useMillesime } from "@src/store/store.millesime";
import { useCallback } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import styles from "./ConformiteAssujettiList.module.scss";

type ConformiteAssujettiListProps = {
  title: string;
  backLink: string;
  avancementType: keyof GatewayAssujetti.Avancement;
};
export function ConformiteAssujettiList({
  title,
  backLink,
  avancementType,
}: ConformiteAssujettiListProps) {
  const hasForbiddenTypeAdhesion = useHasForbiddenTypeAdhesion();
  const { siren } = useParams();
  const { hasAllowedRole } = useIsAllowed();
  const { entreprisesBySiren, status } = useEntreprises();
  const { assujettis, setEntry, removeEntries } = useAssujetti();
  const [query] = useSearchParams();
  const fromAxe = query.get("fromAxe");
  const assujettiArray = assujettis ? Object.values(assujettis) : [];
  const entreprise = entreprisesBySiren?.[siren || ""];
  const { pathname } = useLocation();
  const props = useAssujettiFilter(
    assujettiArray.filter((assujetti) => assujetti.sirenCompany === siren),
    pathname
  );
  const { selected: millesime } = useMillesime();
  const navigate = useNavigate();
  const AssujettiConformite = useCallback(
    ({ assujetti }: { assujetti: Assujetti }) => {
      if (!millesime) return <div />;
      return (
        <TagConformite
          year={millesime}
          isConforme={assujetti.avancement[avancementType] === 100}
        />
      );
    },
    [avancementType, millesime]
  );

  return (
    <MainContentPageTemplate
      title={
        <div className={styles.title}>
          <h1>{title}</h1> <p>{entreprise?.raisonSociale}</p>
        </div>
      }
      actionNavigation={
        hasForbiddenTypeAdhesion([
          GatewayEntreprise.TypeAdhesion.SOCIETE_INDIVIDUELLE,
        ]) ? null : (
          <Link to={fromAxe === null ? `/societes/${siren || ""}` : backLink}>
            <Button variant="text" icon={<ChevronLeft />}>
              Retour
            </Button>
          </Link>
        )
      }
      hasMillesimeNavigation={hasAllowedRole(
        GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR,
        GatewayContact.ContactRoleUtilisateur.RESPONSABLE_CONFORMITE
      )}
      millesime={millesime}
      noEntity={!entreprise && status === StoreEntryFetchStatus.Loaded}
    >
      {status === StoreEntryFetchStatus.Loading && (
        <SkeletonTable size={[1, 1]} />
      )}
      {millesime && (
        <AssujettiList
          {...props}
          millesime={millesime}
          setEntry={setEntry}
          AssujetiConformite={AssujettiConformite}
          removeEntries={removeEntries}
          onRowClick={(assujetti) => {
            navigate(assujetti.id + (fromAxe === null ? "" : "?fromAxe"));
          }}
        />
      )}
    </MainContentPageTemplate>
  );
}
